import type { PanelItem } from '@rta/rta-blueprint-library';

const isWrapper = (panelItem: PanelItem): boolean => panelItem.item.component.sleeveDepth !== null || Boolean(panelItem.item.category?.isInsulatedJacket);

const isTrimKit = (panelItem: PanelItem): boolean => panelItem.item.sku?.endsWith('TK') ?? false;

/** A trim kit, sleeve or insulated jacket -- a part that belongs to a sibling appliance rather than standing on its own. */
export const isCompanionPanelItem = (panelItem: PanelItem): boolean => isWrapper(panelItem) || isTrimKit(panelItem);

/** Appliances that spawn a trim kit declare it as an action rule (e.g. CBIR-L -> 'Fridge21TrimKit'). */
const spawnsTrimKit = (panelItem: PanelItem): boolean => panelItem.item.component.rules.action.some((rule) => rule.includes('TrimKit'));

/**
 * Resolves the appliance a companion belongs to, strongest signal first: the sleeve link, then the
 * fridge whose action rule spawns the trim kit, then the vertically closest non-companion sibling.
 */
const findHostPanelItem = (companion: PanelItem, items: Array<PanelItem>): PanelItem | null => {
	const hosts = items.filter((item) => item !== companion && !isCompanionPanelItem(item));
	if (!hosts.length) return null;

	const wrapped = hosts.find((host) => host.sleeve === companion.item.component.id);
	if (wrapped) return wrapped;

	const trimKitOwners = isTrimKit(companion) ? hosts.filter(spawnsTrimKit) : [];
	const candidates = trimKitOwners.length ? trimKitOwners : hosts;

	return candidates.reduce((closest, host) => (Math.abs(host.center.y - companion.center.y) < Math.abs(closest.center.y - companion.center.y) ? host : closest));
};

/**
 * Reorders an already spatially sorted group so each companion part (fridge trim kit, grill
 * sleeve/insulated jacket) sits directly after the appliance it belongs to. Everything else keeps
 * its incoming order, and a companion with no sibling appliance is left where it was.
 */
export const sortCompanionsAfterAppliances = (sortedItems: Array<PanelItem>): Array<PanelItem> => {
	const companionsByHost = new Map<PanelItem, Array<PanelItem>>();

	sortedItems.filter(isCompanionPanelItem).forEach((companion) => {
		const host = findHostPanelItem(companion, sortedItems);
		if (!host) return;
		companionsByHost.set(host, [...(companionsByHost.get(host) ?? []), companion]);
	});

	if (!companionsByHost.size) return sortedItems;

	const relocated = new Set(Array.from(companionsByHost.values()).flat());
	return sortedItems.filter((item) => !relocated.has(item)).flatMap((item) => [item, ...(companionsByHost.get(item) ?? [])]);
};
